import { nonnull } from "@tento-core/assert";
import { NaiveDate } from "@tento-chrono";
import { AsyncStateManager } from "@tento-core/async-state-manager";
import { LonaIcons, TextIcon } from "@tento-ui/ui/icons";
import { Plot } from "@tento-ui/components/plot";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type SheetRow } from "@tento-lona";
import { type CellEntry, type Option } from "@tento-lona/sheets";
import { ColumnGraphRow } from "@tento-lona/sheets/platform";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { COLUMN_GRAPH_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";

/**
 * The renderer plugin for a graph row: its values plotted across the columns.
 */
export class ColumnGraphRendererPlugin
  implements RenderPlugin.Renderer<Plot, SheetLegend.DefaultRow, ColumnGraphRow> {
  static readonly spec: PerTypeSpec = COLUMN_GRAPH_SPEC;

  readonly role = "renderer";
  readonly id = "column.graph";
  readonly rowClass: typeof ColumnGraphRow = ColumnGraphRow;
  readonly icon: HTMLTemplateElement = LonaIcons.$template(TextIcon);

  #stateManager = new AsyncStateManager();

  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory,
  ) {}

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "Graph simple plots where each column is represented by a single point.";
  }

  makeCell(): Plot {
    return Plot.make().assignStyles({
      height: "100%",
      width: "100%",
      marginBlock: "8px",
    });
  }

  async bindCell(
    $cell: Plot,
    { row, sheetRow, column }: BindCellContext<ColumnGraphRow>,
  ) {
    const time = column.discrete();
    const scale = row.scale;

    const dataPromise = Promise.all(
      time[0]
        .toRange()
        .iter()
        .map((d) => fullySyncedCell(sheetRow, NaiveDate.Partial.fromDate(d))),
    );
    const stillValid = this.#stateManager.bind($cell, dataPromise);
    dataPromise.then((cells) => {
      if (!stillValid(dataPromise)) return;
      this.#stateManager.clear($cell);

      const data = cells.map((cell) => (cell?.value as number ?? 0));
      $cell.bind({
        f: {
          r: {
            start: 0,
            end: data.length,
          },
          data: (idx) => ({ x: idx, y: (data[idx] ?? 0) * scale }),
        },
        tooltipsProvider: (idx) => {
          const prev = data[idx - 1];
          const current = data[idx];
          const delta = prev != null && current != null
            ? current - prev
            : null;
          const date = nonnull(time[0], "a graph column's first period").start.addDays(idx);
          return {
            title: sheetRow.label ?? "Untitled",
            date: NaiveDate.Formatter.render(date),
            value: String(data[idx]),
            change: delta ? (delta > 0 ? `+${delta}` : `${delta}`) : "",
          };
        },
      });
    });
  }

  onResize(_$cell: Plot, _context: BindCellContext<ColumnGraphRow>): void {}

  loadData = null;
  legendFactory: Option<SheetLegendClass<SheetLegend.DefaultRow>> = null;

  onBindRow(_sheet: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}
}

/**
 * A day's cell once its sync completed — `null` when empty, and when the
 * fetch failed (the graph plots it as 0) — with its handle released.
 */
function fullySyncedCell(
  row: SheetRow.Joined.Handle,
  day: NaiveDate.Partial,
): Promise<Option<CellEntry>> {
  return new Promise((resolve) => {
    const handle = row.single()?.cell(day);
    if (handle == null) {
      resolve(null);
      return;
    }
    handle
      .onDataFull((cell) => {
        handle.dispose();
        resolve(cell);
      })
      .onError(() => {
        handle.dispose();
        resolve(null);
      });
  });
}

/** Types that go with `ColumnGraphRendererPlugin`: `Attributes`. */
export namespace ColumnGraphRendererPlugin {
  export type Attributes = {
    scale?: number;
  };
}
